<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { cortexProcessingRunsAPI } from 'dashboard/api/cortex';
import { useAlert } from 'dashboard/composables';
import { cortexProcessingErrorMessage } from 'dashboard/helper/cortexProcessingError';
import NextButton from 'dashboard/components-next/button/Button.vue';
import NextCheckbox from 'dashboard/components-next/checkbox/Checkbox.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextInput from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';

const emit = defineEmits(['saved']);
const { t } = useI18n();
const dialog = ref(null);
const run = ref(null);
const maximumCost = ref('');
const reason = ref('');
const renew = ref(false);
const saving = ref(false);
const refreshing = ref(false);
const stale = ref(false);
const errorMessage = ref('');
let operation;

const committedCost = computed(() =>
  Number(
    run.value?.budget?.committed_cost ??
      Number(run.value?.actual_cost || 0) +
        Number(run.value?.budget?.reserved_cost || 0)
  )
);
const formattedCommitted = computed(() =>
  new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 6,
  }).format(committedCost.value)
);
const formattedExpiry = computed(() =>
  run.value?.authorization_ends_at
    ? new Intl.DateTimeFormat(undefined, {
        dateStyle: 'short',
        timeStyle: 'short',
      }).format(new Date(run.value.authorization_ends_at))
    : t('CORTEX.PROCESSING.V6.NOT_AVAILABLE')
);
const canSave = computed(() => {
  const amount = String(maximumCost.value).trim();
  const note = reason.value.trim();
  return (
    run.value?.can_manage_budget &&
    !saving.value &&
    !refreshing.value &&
    !stale.value &&
    /^\d+(?:\.\d{1,6})?$/.test(amount) &&
    Number(amount) < 1_000_000 &&
    Number(amount) >= committedCost.value &&
    note.length >= 10 &&
    note.length <= 500
  );
});

const open = selectedRun => {
  if (saving.value) return;
  run.value = { ...selectedRun };
  maximumCost.value = String(selectedRun.budget_cost);
  reason.value = '';
  renew.value = false;
  stale.value = false;
  errorMessage.value = '';
  operation = null;
  dialog.value?.open();
};

const save = async () => {
  if (!canSave.value) return;
  const data = {
    maximum_cost: String(maximumCost.value).trim(),
    reason: reason.value.trim(),
    renew_authorization: renew.value,
    lock_version: run.value.lock_version,
  };
  const signature = JSON.stringify(data);
  if (operation?.signature !== signature)
    operation = {
      signature,
      key: `budget-${crypto.getRandomValues(new Uint32Array(4)).join('-')}`,
    };
  saving.value = true;
  errorMessage.value = '';
  try {
    await cortexProcessingRunsAPI.adjustBudget(
      run.value.id,
      data,
      operation.key
    );
    dialog.value?.close();
    emit('saved', run.value.id);
    useAlert(t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.SUCCESS'));
  } catch (error) {
    stale.value = error?.response?.status === 409;
    errorMessage.value = stale.value
      ? t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.STALE')
      : cortexProcessingErrorMessage(
          error,
          t,
          t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.ERROR')
        );
  } finally {
    saving.value = false;
  }
};

const refresh = async () => {
  refreshing.value = true;
  try {
    const { data } = await cortexProcessingRunsAPI.show(run.value.id);
    run.value = data;
    stale.value = false;
    errorMessage.value = '';
    operation = null;
  } catch (error) {
    errorMessage.value = t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.ERROR');
  } finally {
    refreshing.value = false;
  }
};

defineExpose({ open });
</script>

<template>
  <Dialog
    ref="dialog"
    width="md"
    overflow-y-auto
    :title="$t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.TITLE', { id: run?.id })"
    :description="$t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.DESCRIPTION')"
    :confirm-button-label="$t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.SAVE')"
    confirm-button-icon="i-lucide-save"
    cancel-button-icon="i-lucide-x"
    :disable-confirm-button="!canSave"
    :is-loading="saving"
    @confirm="save"
  >
    <div v-if="run" class="grid gap-4">
      <p class="text-label-small leading-4 text-n-slate-11">
        {{
          $t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.COMMITTED', {
            amount: formattedCommitted,
          })
        }}
      </p>
      <NextInput
        v-model="maximumCost"
        type="number"
        min="0"
        max="999999.999999"
        step="0.000001"
        :label="$t('CORTEX.PROCESSING.V6.MAXIMUM_COST')"
        :message="$t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.LIMIT_HELP')"
        :disabled="saving"
      />
      <TextArea
        id="cortex-processing-budget-reason"
        v-model="reason"
        :label="$t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.REASON')"
        :message="$t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.REASON_HELP')"
        :disabled="saving"
        :max-length="500"
      />
      <label class="flex cursor-pointer items-start gap-2.5">
        <NextCheckbox v-model="renew" class="mt-0.5" :disabled="saving" />
        <span class="grid gap-1 text-sm text-n-slate-12">
          {{ $t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.RENEW') }}
          <span class="text-label-small leading-4 text-n-slate-11">
            {{
              $t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.EXPIRY', {
                date: formattedExpiry,
              })
            }}
          </span>
        </span>
      </label>
      <p v-if="errorMessage" role="alert" class="text-sm text-n-ruby-11">
        {{ errorMessage }}
      </p>
      <NextButton
        v-if="stale"
        type="button"
        slate
        ghost
        icon="i-lucide-refresh-cw"
        :label="$t('CORTEX.PROCESSING.V6.BUDGET_MANAGEMENT.REFRESH')"
        :is-loading="refreshing"
        @click="refresh"
      />
    </div>
  </Dialog>
</template>
